import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import type { ReactNode } from 'react';
import type Stripe from 'stripe-latest';

import {
  fossaPlanMap,
  FREE_PLAN_NAME,
  isDynamicPlan,
} from '../../../../../shared/subscriptions';
import { getBaseOrgProperties, track } from '../../../../helpers/analytics';
import { useGetCurrentPlan } from '../../../../hooks/subscriptions/useGetCurrentPlan';
import { calculateDiscountPercentage, getBusinessBucketSizeLabel } from '../utils';
import type {
  Plan,
  StaticPlanName,
} from '../../../../../shared/subscriptions';
import type { FossaSubscriptionPlan } from '../../../../../typings/common';

import usePlans from './usePlans';
import type { ViewIntervalOptions } from './index';

type PlansContextType = {
  businessDiscount: number | null,
  businessPrices: Stripe.Price[],
  currentPlan?: FossaSubscriptionPlan,
  enterprisePrice: Stripe.Price,
  filteredBusinessPrices: Stripe.Price[],
  freePrice: Stripe.Price,
  getPlanName: (priceId?: string) => string,
  handleBusinessPriceChange: (priceId: string) => void,
  isLoading: boolean,
  plans?: Plan[];
  selectedBusinessPrice: Stripe.Price | null,
  setViewInterval: React.Dispatch<React.SetStateAction<ViewIntervalOptions>>;
  viewInterval: ViewIntervalOptions,
};

type PriceAndPlanReturn = {
  plan?: Plan,
  price?: Stripe.Price,
};

const PlansContext = createContext<PlansContextType | undefined>(undefined);

const DEFAULT_INTERVAL: ViewIntervalOptions = 'year';

export const PlansProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [freePrice, setFreePrice] = useState<Stripe.Price>({} as Stripe.Price);
  const [businessPrices, setBusinessPrices] = useState<Stripe.Price[]>([]);
  const [enterprisePrice, setEnterprisePrice] = useState<Stripe.Price>({} as Stripe.Price);
  const [filteredBusinessPrices, setFilteredBusinessPrices] = useState<Stripe.Price[]>([]);
  const [selectedBusinessPrice, setSelectedBusinessPrice] = useState<Stripe.Price | null>(null);
  const [viewInterval, setViewInterval] = useState<ViewIntervalOptions>('year');

  const {
    data: currentPlan,
    isLoading: isLoadingCurrentPlan,
  } = useGetCurrentPlan();

  const {
    data: plans,
    isLoading,
  } = usePlans();

  const getPriceAndPlan = useMemo(() => (priceId?: string): PriceAndPlanReturn => {
    if (!plans || !priceId) {
      return {};
    }

    const plan = plans.find((p) => p.prices.find((price) => price.id === priceId));
    if (!plan) {
      return {};
    }

    const price = plan.prices.find((p => p.id === priceId));

    return { plan, price };
  }, [plans]);

  useEffect(() => {
    // Update the state once the fetched plans are available
    if (isLoading || isLoadingCurrentPlan || !currentPlan || !plans || plans.length === 0) {
      return;
    }

    const freePrices = plans
      .filter(plan => plan.type === 'FREE')
      .flatMap(plan => plan.prices);
    setFreePrice(freePrices[0] as Stripe.Price);

    const businessPricesList = plans
      .filter(plan => plan.type === 'BUSINESS')
      .flatMap(plan => plan.prices);
    setBusinessPrices(businessPricesList);

    const activePrices = businessPricesList.filter(price => price?.recurring?.interval === viewInterval);
    setFilteredBusinessPrices(activePrices);

    const { price: currentPrice } = getPriceAndPlan(currentPlan.plan);
    const { price: nextPrice } = getPriceAndPlan(currentPlan.nextScheduledPrice?.id);

    if (nextPrice && activePrices.find((p) => nextPrice.id === p.id)) {
      // Set current selected to next scheduled price if it is business plan
      setSelectedBusinessPrice(nextPrice);
    } else if (currentPrice && activePrices.find((p) => currentPrice.id === p.id)) {
      // Set current selected to current plan if nothing scheduled and current is business plan
      setSelectedBusinessPrice(currentPrice);
    } else {
      setSelectedBusinessPrice(activePrices[0] as Stripe.Price);
    }

    const enterprisePrices = plans
      .filter(plan => plan.type === 'ENTERPRISE')
      .flatMap(plan => plan.prices);
    setEnterprisePrice(enterprisePrices[0] as Stripe.Price);
  }, [currentPlan, getPriceAndPlan, plans, isLoading, isLoadingCurrentPlan, viewInterval]);

  /**
   * Calculate the discount percentage between the yearly & monthly rate for the selected price
   */
  const getBusinessDiscount = useMemo(() => () => {
    if (!selectedBusinessPrice) {
      return null;
    }

    if (selectedBusinessPrice.recurring?.interval === 'month') {
      return null;
    }

    const monthlyPrice = businessPrices.find((p: Stripe.Price) =>
      ((p.product as Stripe.Product).name && (p.product as Stripe.Product).id
        === (selectedBusinessPrice.product as Stripe.Product).id));

    if (!monthlyPrice) {
      return null;
    }

    if (!monthlyPrice.unit_amount || !selectedBusinessPrice.unit_amount) {
      return null;
    }

    return calculateDiscountPercentage((monthlyPrice.unit_amount * 12), selectedBusinessPrice.unit_amount);
  }, [businessPrices, selectedBusinessPrice]);

  const getPlanName = useMemo(() => (priceId?: string) => {
    const { price } = getPriceAndPlan(priceId);
    if (priceId && !isDynamicPlan(priceId)) {
      const staticPlan = fossaPlanMap[priceId as StaticPlanName];
      if (!staticPlan) {
        return 'N/A';
      }
      return `${staticPlan.label}: ${staticPlan.description}`;
    }

    if (!price) {
      return 'N/A';
    }
    return (price.product as Stripe.Product).name;
  }, [getPriceAndPlan]);

  const getCurrentPlanIntervalOrDefault: () => ViewIntervalOptions = useMemo(() => () => {
    if (!currentPlan) {
      return DEFAULT_INTERVAL;
    }

    if (currentPlan.plan === freePrice.id || currentPlan.plan === FREE_PLAN_NAME) {
      return DEFAULT_INTERVAL;
    }

    const { price } = getPriceAndPlan(currentPlan.plan);
    if (!price) {
      return DEFAULT_INTERVAL;
    }

    return price?.recurring?.interval as ViewIntervalOptions || DEFAULT_INTERVAL;
  }, [currentPlan, freePrice, getPriceAndPlan]);

  useEffect(() => {
    setViewInterval(getCurrentPlanIntervalOrDefault());
  }, [getCurrentPlanIntervalOrDefault]);

  const handleBusinessPriceChange = useMemo(() => (priceId: string) => {
    const price = businessPrices.find((p: Stripe.Price) => p.id === priceId) as Stripe.Price || null;
    setSelectedBusinessPrice(price);
    const tier = getBusinessBucketSizeLabel((price.product as Stripe.Product).name);
    track(`Plan Tier - ${tier}`, { ...getBaseOrgProperties(), tier });
  }, [businessPrices]);

  const contextValue = useMemo((): PlansContextType => ({
    businessDiscount: getBusinessDiscount(),
    businessPrices,
    currentPlan,
    enterprisePrice,
    filteredBusinessPrices,
    freePrice,
    getPlanName,
    handleBusinessPriceChange,
    isLoading: isLoading && isLoadingCurrentPlan,
    plans,
    selectedBusinessPrice,
    setViewInterval,
    viewInterval,
  }), [
    businessPrices,
    currentPlan,
    enterprisePrice,
    filteredBusinessPrices,
    freePrice,
    getBusinessDiscount,
    getPlanName,
    handleBusinessPriceChange,
    isLoading,
    isLoadingCurrentPlan,
    plans,
    selectedBusinessPrice,
    setViewInterval,
    viewInterval,
  ]);

  return (
    <PlansContext.Provider value={contextValue}>
      {children}
    </PlansContext.Provider>
  );
};

// Create a custom hook to use the shared plans context
export const usePlansContext = (): PlansContextType => {
  const context = useContext(PlansContext);
  if (context === undefined) {
    throw new Error('usePlans must be used within a PlansProvider');
  }
  return context;
};
